/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Sigma, Layers, ChevronRight } from 'lucide-react';
import { Preset, RowData } from './types';
import { generateRows } from './algorithm';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import NumberNode from './components/NumberNode';
import ShiftLines from './components/ShiftLines';
import Prop9Panel from './components/Prop9Panel';
import Footer from './components/Footer';
import SEO from './components/SEO';
import AboutModal from './components/AboutModal';

export default function App() {
  const [root, setRoot] = useState<number>(1);
  const [base, setBase] = useState<number>(2);
  const [power, setPower] = useState<number>(3);
  const [weight, setWeight] = useState<number>(1);
  const [method, setMethod] = useState<'full' | 'contracted'>('contracted');
  const [activeLevel, setActiveLevel] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [showPolarity, setShowPolarity] = useState<boolean>(false);
  const [showProp9, setShowProp9] = useState<boolean>(false);
  const [showShiftLines, setShowShiftLines] = useState<boolean>(true);
  const [showAbout, setShowAbout] = useState<boolean>(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  const rows = useMemo(
    () => generateRows(root, base, power, weight, method),
    [root, base, power, weight, method]
  );

  const prop9Data = useMemo(() => {
    if (base <= 1) return null;
    const sumOfAllEntries = rows
      .slice(0, activeLevel + 1)
      .reduce((acc, row) => acc + row.sum, 0);
    const rootTerm = root / (base - 1);
    const nextPowerVal = root * Math.pow(base, activeLevel + 1);
    return {
      sumOfAllEntries: Number(sumOfAllEntries.toFixed(4)),
      rootTerm: Number(rootTerm.toFixed(4)),
      lhs: Number((sumOfAllEntries + rootTerm).toFixed(4)),
      rhs: Number((nextPowerVal / (base - 1)).toFixed(4)),
      nextPowerVal: Number(nextPowerVal.toFixed(4)),
    };
  }, [rows, activeLevel, root, base]);

  const applyPreset = (preset: Preset) => {
    setRoot(preset.root);
    setBase(preset.base);
    setPower(preset.power);
    setWeight(preset.weight);
    setMethod(preset.method);
    setActiveLevel(0);
    setIsPlaying(false);
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying) {
      interval = setInterval(() => {
        setActiveLevel((prev) => {
          if (prev >= power) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, power]);

  const reset = () => {
    setActiveLevel(0);
    setIsPlaying(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] font-sans text-zinc-300 selection:bg-emerald-500/30">
      <SEO />
      <AboutModal isOpen={showAbout} onClose={() => setShowAbout(false)} />
      {/* Background decoration */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] h-[40%] w-[40%] rounded-full bg-emerald-500/5 blur-[120px]" />
        <div className="absolute right-[-10%] bottom-[-10%] h-[40%] w-[40%] rounded-full bg-blue-500/5 blur-[120px]" />
      </div>

      <div className="relative flex h-[100dvh] h-screen flex-col">
        <Header
          isPlaying={isPlaying}
          setIsPlaying={setIsPlaying}
          reset={reset}
          onAboutClick={() => setShowAbout(true)}
          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        />

        <main className="flex flex-1 overflow-hidden">
          <Sidebar
            root={root}
            setRoot={setRoot}
            base={base}
            setBase={setBase}
            power={power}
            setPower={setPower}
            weight={weight}
            setWeight={setWeight}
            method={method}
            setMethod={setMethod}
            activeLevel={activeLevel}
            setActiveLevel={setActiveLevel}
            showPolarity={showPolarity}
            setShowPolarity={setShowPolarity}
            showProp9={showProp9}
            setShowProp9={setShowProp9}
            showShiftLines={showShiftLines}
            setShowShiftLines={setShowShiftLines}
            applyPreset={applyPreset}
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
          />

          <div className="bg-grid-zinc-900/50 relative flex flex-1 flex-col overflow-hidden">
            <div className="absolute top-4 right-4 z-10 flex flex-col items-end gap-3 md:top-6 md:right-8">
              <div className="flex items-center gap-3 rounded-full border border-zinc-800 bg-zinc-900/80 px-4 py-2 shadow-2xl backdrop-blur-md">
                <div className="flex items-center gap-1.5 md:gap-2">
                  <Sigma className="h-4 w-4 text-emerald-500" />
                  <span className="font-mono text-xs text-zinc-400">
                    Current Sum:
                  </span>
                  <span className="font-mono text-xs font-bold text-white">
                    {rows[activeLevel]?.sum}
                  </span>
                </div>
                <div className="h-4 w-px bg-zinc-800" />
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-blue-500" />
                  <span className="font-mono text-xs text-zinc-400">
                    Level:
                  </span>
                  <span className="font-mono text-xs font-bold text-white">
                    {activeLevel}
                  </span>
                </div>
              </div>

              <Prop9Panel show={showProp9} data={prop9Data} base={base} />
            </div>

            <div className="custom-scrollbar flex flex-1 flex-col items-center overflow-auto p-6 md:p-12">
              <div className="relative flex min-w-max flex-col items-center gap-8">
                {showShiftLines && (
                  <ShiftLines
                    rows={rows}
                    activeLevel={activeLevel}
                    base={base}
                    method={method}
                  />
                )}

                <AnimatePresence mode="popLayout">
                  {rows.slice(0, activeLevel + 1).map((row, rIdx) => (
                    <motion.div
                      key={`row-${rIdx}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="flex flex-col items-center gap-3"
                    >
                      <div className="mb-1 flex items-center gap-2">
                        <span className="font-mono text-[10px] tracking-tighter text-zinc-600 uppercase">
                          Power {rIdx}
                        </span>
                        <div className="h-px w-8 bg-zinc-800" />
                      </div>

                      <div className="relative flex items-center justify-center">
                        <div className="flex items-center justify-center gap-4">
                          {row.elements.map((val, eIdx) => (
                            <NumberNode
                              key={`node-${rIdx}-${eIdx}`}
                              value={val}
                              index={eIdx}
                              total={row.elements.length}
                              showPolarity={showPolarity}
                            />
                          ))}
                        </div>

                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.5 }}
                          className="absolute left-full ml-6 flex items-center gap-2 whitespace-nowrap text-zinc-500"
                        >
                          <ChevronRight className="h-4 w-4" />
                          <div className="rounded border border-zinc-800 bg-zinc-800/30 px-2 py-1">
                            <span className="font-mono text-xs font-bold text-zinc-400">
                              Σ = {row.sum}
                            </span>
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>

            <div className="border-t border-zinc-800/50 bg-zinc-900/20 px-4 py-4 backdrop-blur-md md:px-8 md:py-6">
              <div className="flex items-center gap-4">
                <span className="font-mono text-xs text-zinc-500">
                  Timeline
                </span>
                <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-800">
                  <motion.div
                    className="absolute inset-y-0 left-0 bg-emerald-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${(activeLevel / power) * 100}%` }}
                    transition={{ type: 'spring', bounce: 0 }}
                  />
                  <div className="absolute inset-0 flex justify-between px-1">
                    {Array.from({ length: power + 1 }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveLevel(i)}
                        className={`z-10 -mt-[3px] h-3 w-3 rounded-full border-2 transition-all ${
                          i <= activeLevel
                            ? 'border-emerald-500 bg-emerald-500'
                            : 'border-zinc-700 bg-zinc-900'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <span className="font-mono text-xs text-zinc-500">
                  {activeLevel} / {power}
                </span>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>

      <style>{`
        .bg-grid-zinc-900\\/50 {
          background-image: radial-gradient(circle, #18181b 1px, transparent 1px);
          background-size: 32px 32px;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #27272a;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #3f3f46;
        }
      `}</style>
    </div>
  );
}
